import { useLocation } from "react-router-dom";
import { usePost } from "../../API";
import React, { Suspense, useCallback, useRef, useState } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { Button, Card, CardBody, Icon, useToast } from "@aidkitorg/component-library";
import { getDistroBrowserIncompatibility } from "../../Util";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

const blankRubric: survey.Rubric = {
  kind: 'Rubric',
  name: '',
  version: 0,
  description: '',
  graders: []
};

type TemplateOption = {
  label: string;
  value: string;
  name: string;
  version: number;
};

export function RubricConfig() {
  const listRubricsRequest = usePost('/program/admin/rubrics/list');
  const [loadedRubric, setLoadedRubric] = useState<survey.Rubric | null>(null);
  const location = useLocation();

  useAsyncEffect(async () => {
    const queryParams = new URLSearchParams(location.search);
    const name = queryParams.get('name');
    const version = queryParams.get('version');

    if (name) {
      const { items: rubrics } = await listRubricsRequest({
        name,
        version: version ? parseInt(version) : undefined
      });
      const existingRubric = rubrics?.[rubrics?.length - 1];
      if (existingRubric) {
        setLoadedRubric({ ...existingRubric, kind: 'Rubric' } as survey.Rubric);
        return;
      }
    }

    setLoadedRubric(blankRubric);
  }, []);

  if (!loadedRubric) return <div>Loading...</div>;

  return <InnerRubricConfig initial={loadedRubric} />;
}

export function InnerRubricConfig(props: { initial: survey.Rubric }) {
  const [rubric, setRubric] = useState<survey.Rubric>(props.initial);
  const [latestVersion, setLatestVersion] = useState(props.initial.version);
  const [availableTemplates, setAvailableTemplates] = useState<TemplateOption[]>([]);
  const [previousRubricVariables, setPreviousRubricVariables] = useState(new Set(rubric.variables?.map(v => v.key) ?? []));
  const [needsNewEvalId, setNeedsNewEvalId] = useState(false);

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const createRubricRequest = usePost('/program/admin/rubrics/create');
  const listRubricsRequest = usePost('/program/admin/rubrics/list');
  const scanTemplatesRequest = usePost('/program/admin/prompts/scan');

  const { toast } = useToast();

  // Load available message templates for grader templateRef references
  useAsyncEffect(async () => {
    const { nameToVersionsAndDescriptionsMap } = await scanTemplatesRequest({ latestOnly: true });

    const options: TemplateOption[] = Object.entries(nameToVersionsAndDescriptionsMap).map(([name, versionsAndDescriptions]) => ({
      label: `${name} (v${versionsAndDescriptions[0].version})`,
      value: `${name}:${versionsAndDescriptions[0].version}`,
      name: name,
      version: versionsAndDescriptions[0].version
    }));

    setAvailableTemplates(options);
  }, []);

  // Track latest version when rubric name changes
  useAsyncEffect(async () => {
    const name = rubric.name?.trim();
    if (!name) {
      setLatestVersion(0);
      return;
    }

    const result = await listRubricsRequest({ name });
    const latest = (result.items ?? [])
      .map((item: { version: number }) => item.version)
      .filter((v: unknown) => typeof v === 'number')
      .sort((a: number, b: number) => b - a)[0] ?? 0;

    setLatestVersion(latest);
  }, [rubric.name]);

  const saveRubric = useCallback(async () => {
    if (!rubric.name?.trim()) {
      toast({
        description: 'Please enter a rubric name',
        autoClose: true,
        duration: 2000
      });
      return;
    }

    const oldVersion = rubric.version ?? 0;
    const newVersion = (latestVersion ?? oldVersion) + 1;

    // Note: The backend will create/update the OpenAI Eval and set evalId
    const result = await createRubricRequest({
      rubric: { ...rubric, version: newVersion },
      needsNewEvalId: needsNewEvalId,  
    });

    if (result.success) {
      // Update local state with the new evalId from OpenAI
      const updatedRubric = { ...rubric, version: newVersion };
      setRubric(updatedRubric);
      setLatestVersion(newVersion);
      setNeedsNewEvalId(false);
      toast({
        description: oldVersion > 0
          ? `Updated rubric: v${oldVersion} → v${newVersion}`
          : 'Created new rubric!',
        autoClose: true,
        duration: 1500
      });
    }
  }, [createRubricRequest, rubric, latestVersion, toast]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  console.log(rubric);

  const onDistroChangeHandler = useCallback((v: survey.Rubric) => {
    const newRubricVariables = v.variables?.map(v => v.key) ?? [];
    if (newRubricVariables.length !== previousRubricVariables.size || !newRubricVariables.every(v => previousRubricVariables.has(v))) {
      setNeedsNewEvalId(true);
      setPreviousRubricVariables(new Set(newRubricVariables));
    } else {
      setNeedsNewEvalId(false);
    }
    setRubric(v);
  }, [previousRubricVariables]);

  return (
    <div className="bg-gray-100 h-full p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Rubric Configuration</h1>
      </div>

      <div className={`grid gap-4 grid-cols-1`}>
        <div className="bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
          <h2 className="text-lg font-semibold">Rubric Editor</h2>

          <Card className="m-2 bg-blue-50">
            <CardBody>
              {needsNewEvalId
                ? <p>Rubric variables have changed. Please save the rubric to update the OpenAI Eval.</p>
                : rubric.evalId
                  ? <p>OpenAI Eval ID: {rubric.evalId}</p>
                  : <p>No OpenAI Eval ID yet. Save to generate one.</p>
              }
            </CardBody>
          </Card>

          <Suspense fallback={<div>Loading editor...</div>}>
            <Distro
              ref={distroRef}
              value={rubric}
              types='src/survey.ts'
              llmContext={`We are configuring a Rubric for evaluating AI conversation outputs.

A Rubric corresponds to an OpenAI Eval and contains graders (testing criteria).

**Grader Types:**
- Score Model Grader: Uses a model to assign a numeric score (0-1 by default)

**Template Variables for Grader Input:**
- {{item.step_name}} - References named step outputs from the ConversationGenerator
- {{sample.output_text}} - References the final model output being evaluated

**Grader Fields:**
- name: Identifier for this grader
- templateRef: Optional reference to a Message Template for the grading prompt
- input: Inline messages if not using templateRef (supports template variables)
- model: The model to use for grading (e.g., gpt-4o, gpt-4.1)
- reasoningEffort: For reasoning models (low/medium/high/minimal)
- temperature: For non-reasoning models (0-2)
- range: Score range, defaults to [0, 1]
- passThreshold: Minimum score to pass

Available Message Templates for templateRef: ${availableTemplates.map(t => t.name).join(', ')}`}
              name='Rubric'
              onChange={onDistroChangeHandler}
            />
          </Suspense>

          <div className='flex flex-col items-center gap-8'>
            <div className='flex flex-row gap-4'>
              <Button
                className="ml-2 mb-2"
                variant="primary"
                onClick={saveRubric}
                isDisabled={!rubric.name?.trim()}
              >
                {`Save Rubric (v${(latestVersion ?? rubric.version) + 1})`}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
